import { useCallback } from 'react';

import axios from 'axios';
import { isObjectLike } from 'lodash';

import type { DialplanDataBase } from '../dataProvider/types';
import type { OrdJsonErrorResponse } from '@ragu-components/ord-types';
import type { Node, ReactFlowInstance } from '@xyflow/react';

type JsonApiError = NonNullable<OrdJsonErrorResponse['errors']>[number];

interface NodeServerValidation {
  messages: string[];
}

export const isHttp402Error = (error: unknown): boolean => {
  return isObjectLike(error) && (error as { status?: unknown }).status === 402;
};
const isOrdJsonErrorResponse = (value: unknown): value is OrdJsonErrorResponse =>
  isObjectLike(value) && Array.isArray((value as { errors?: unknown }).errors);

const mapValidationErrorsObject = (errorsObject: Record<string, unknown>): JsonApiError[] =>
  Object.entries(errorsObject).reduce<JsonApiError[]>((acc, [sourcePath, message]) => {
    if (typeof message !== 'string') {
      return acc;
    }
    acc.push({
      title: 'Invalid value',
      source: { pointer: `/${sourcePath.replace(/\./g, '/')}` },
      detail: message,
    });
    return acc;
  }, []);

export const extractJsonApiErrors = (error: unknown): JsonApiError[] => {
  if (axios.isAxiosError<OrdJsonErrorResponse>(error)) {
    const responseData = error.response?.data;
    if (isOrdJsonErrorResponse(responseData)) {
      return responseData.errors ?? [];
    }
    const responseDataUnknown: unknown = responseData;
    if (isObjectLike(responseDataUnknown)) {
      const responseErrors = (responseDataUnknown as { errors?: unknown }).errors;
      if (isObjectLike(responseErrors)) {
        return mapValidationErrorsObject(responseErrors as Record<string, unknown>);
      }
    }
  }

  if (!isObjectLike(error)) {
    return [];
  }

  const response = (error as { response?: unknown }).response;
  const responseData = isObjectLike(response) ? (response as { data?: unknown }).data : undefined;
  const candidates = [
    error,
    (error as { body?: unknown }).body,
    responseData,
    response,
    (error as { cause?: unknown }).cause,
  ];

  for (const candidate of candidates) {
    if (isOrdJsonErrorResponse(candidate)) {
      return candidate.errors ?? [];
    }

    if (isObjectLike(candidate) && isOrdJsonErrorResponse((candidate as { data?: unknown }).data)) {
      return (candidate as { data: OrdJsonErrorResponse }).data.errors ?? [];
    }

    if (isObjectLike(candidate) && isObjectLike((candidate as { errors?: unknown }).errors)) {
      const mappedErrors = mapValidationErrorsObject((candidate as { errors: Record<string, unknown> }).errors);
      if (mappedErrors.length > 0) {
        return mappedErrors;
      }
    }
  }

  return [];
};

export const clearServerValidationFromNodes = (nodes: Node[]): Node[] =>
  nodes.map((node) => ({
    ...node,
    data: {
      ...node.data,
      serverErrors: undefined,
    },
  }));

export const mapServerValidationErrorsToNodes = (
  errors: JsonApiError[],
  backendData: DialplanDataBase
): Map<string, NodeServerValidation> => {
  const nodeValidationById = new Map<string, NodeServerValidation>();
  const ensureNodeValidation = (nodeId: string): NodeServerValidation => {
    const existing = nodeValidationById.get(nodeId);
    if (existing !== undefined) {
      return existing;
    }
    const created: NodeServerValidation = {
      messages: [],
    };
    nodeValidationById.set(nodeId, created);
    return created;
  };

  for (const error of errors) {
    const pointer = error.source?.pointer;
    if (pointer === undefined || pointer === '') {
      continue;
    }

    const pathSegments = pointer.split('/').filter(Boolean);
    if (pathSegments.length === 0) {
      continue;
    }

    let nodeId: string | undefined;
    if (pathSegments[0] === 'entry') {
      nodeId = backendData.entry.node_id;
    } else if (pathSegments[0] === 'nodes') {
      const nodeIndex = Number(pathSegments[1]);
      if (Number.isNaN(nodeIndex)) {
        continue;
      }
      nodeId = backendData.nodes[nodeIndex]?.node_id;
    }

    if (nodeId === undefined) {
      continue;
    }

    const nodeValidation = ensureNodeValidation(nodeId);
    const message = error.detail ?? error.title;
    if (message !== undefined && message !== '' && !nodeValidation.messages.includes(message)) {
      nodeValidation.messages.push(message);
    }
  }

  return nodeValidationById;
};

export const useServerValidationErrors = (
  reactFlowInstance: ReactFlowInstance<Node>
): {
  clearServerValidation: () => void;
  applyServerValidationErrors: (error: unknown, backendData: DialplanDataBase) => void;
} => {
  const clearServerValidation = useCallback(() => {
    reactFlowInstance.setNodes((nodes) => clearServerValidationFromNodes(nodes));
  }, [reactFlowInstance]);

  const applyServerValidationErrors = useCallback(
    (error: unknown, backendData: DialplanDataBase) => {
      const jsonApiErrors = extractJsonApiErrors(error);
      const errorsByNode = mapServerValidationErrorsToNodes(jsonApiErrors, backendData);

      if (errorsByNode.size === 0) {
        return;
      }

      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          const nodeValidation = errorsByNode.get(node.id);
          if (nodeValidation === undefined) {
            return node;
          }
          return {
            ...node,
            data: {
              ...node.data,
              serverErrors: nodeValidation.messages,
            },
          };
        })
      );
    },
    [reactFlowInstance]
  );

  return {
    clearServerValidation,
    applyServerValidationErrors,
  };
};
